import React from 'react';
import { Modal, Button, notification } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { useReportGenerate } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useDiagnosticReport } from '@/core/domain/reports/movements/diagnostics/Report.hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { Diagnostic } from '@/core/domain/workorder/Diagnostics/Diagnostics.types';
import generateCsv from '@/utils/functions/generateCsv';
import moment from 'moment';

type Props = {
  visible: boolean;
  handleVisible: () => void;
  diagnostic?: Diagnostic;
};

const DiagnosticReportModal: React.FC<Props> = ({
  visible,
  handleVisible,
  diagnostic,
}) => {
  const intl = useIntl();
  const { diagnosticId } = useRouteMatch<{ diagnosticId: string }>().params;

  const diagnosticReport = useDiagnosticReport();

  const handleCsv = () => {
    const header = [
      [
        diagnostic?.company.name || '',
        'Id do diagnóstico',
        diagnosticId || '',
        'Funcionário',
        diagnostic?.employee.name || '',
        'Emissão',
        moment().format('DD/MM/YYYY hh:mm:ss').toString(),
      ],
      [
        'Cliente',
        diagnostic?.customer.name || '',
        'CPF/CNPJ',
        diagnostic?.customer.document || '',
      ],
      [
        'Veículo',
        `${diagnostic?.vehicle.brand} ${diagnostic?.vehicle.model}`,
        'Placa',
        diagnostic?.vehicle.licensePlate || '',
      ],
      [],
    ];
    const footer = [[], ['Observação', diagnostic?.clientObservation || '']];
    const csvData = diagnostic?.items.map(({ name, status }) => ({
      ['Item']: name,
      ['Situação']: status.includes('change')
        ? 'Substituir'
        : status.includes('ok')
        ? 'Ok'
        : status.includes('repair')
        ? 'Reparar'
        : '',
    }));
    if (csvData) {
      generateCsv(csvData, 'Diagnóstico', header, footer);
    }
  };

  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();

  const report = useReportGenerate();

  const handleCancel = () => {
    handleVisible();
  };

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const obj = {
        format: FILE_FORMAT[format],
        diagnosticId: Number(diagnosticId),
      };

      const result = await diagnosticReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: 'Sucesso!',
        description: 'Relatório de diagnóstico gerado com sucesso!',
      });
      handleVisible();
    } catch {}
    setFileUnloading(format);
  };
  const footer = [
    <SpaceForButton justifyContent="flex-end">
      <Button
        block
        type="primary"
        onClick={() =>
          handleGenerateReport('REPORT_VIEW', (result) =>
            report.openFile({
              result,
              fileName: 'diagnostico.pdf',
              fileType: 'application/pdf',
            }),
          )
        }
        key="viewReport"
        loading={fileLoading.REPORT_VIEW}
      >
        <FormattedMessage id="customers.modal.report.buttons.viewReport" />
      </Button>
      <Button
        block
        type="primary"
        key="PDF"
        onClick={() =>
          handleGenerateReport('PDF', (result) =>
            report.downloadFile({ result, fileName: 'diagnostico.pdf' }),
          )
        }
        loading={fileLoading.PDF}
      >
        <FormattedMessage id="customers.modal.report.buttons.pdf" />
      </Button>
      <Button block type="primary" key="CSV" onClick={handleCsv}>
        <FormattedMessage id="customers.modal.report.buttons.csv" />
      </Button>
    </SpaceForButton>,
  ];

  return (
    <Modal
      title={intl.formatMessage({ id: 'diagnostics.modal.report.title' })}
      open={visible}
      footer={footer}
      onCancel={handleCancel}
      width={550}
    >
      <p>
        {intl.formatMessage({
          id: 'quotations.details.report.selectReportType',
        })}
      </p>
    </Modal>
  );
};

export default DiagnosticReportModal;
